/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { RelatedContentRepresentation, ResultListDataRepresentationRelatedContentRepresentation } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
export class ContentApi extends BaseApi {
    createRelatedContentOnProcessInstance(processInstanceId, relatedContent, opts) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        throwIfNotDefined(relatedContent, 'relatedContent');
        opts = opts || {};
        const pathParams = {
            processInstanceId
        };
        if (relatedContent instanceof RelatedContentRepresentation) {
            return this.post({
                path: '/api/enterprise/process-instances/{processInstanceId}/content',
                pathParams,
                queryParams: opts,
                bodyParam: relatedContent,
                returnType: RelatedContentRepresentation
            });
        }
        else {
            const formParams = {
                file: relatedContent
            };
            return this.post({
                path: '/api/enterprise/process-instances/{processInstanceId}/raw-content',
                pathParams,
                formParams,
                queryParams: opts,
                contentTypes: ['multipart/form-data'],
                returnType: RelatedContentRepresentation
            });
        }
    }
    createRelatedContentOnTask(taskId, relatedContent, opts) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(relatedContent, 'relatedContent');
        const pathParams = {
            taskId
        };
        if (relatedContent instanceof RelatedContentRepresentation) {
            return this.post({
                path: '/api/enterprise/tasks/{taskId}/content',
                pathParams,
                queryParams: opts,
                bodyParam: relatedContent,
                returnType: RelatedContentRepresentation
            });
        }
        else {
            const formParams = {
                file: relatedContent
            };
            return this.post({
                path: '/api/enterprise/tasks/{taskId}/raw-content',
                pathParams,
                queryParams: opts,
                formParams,
                contentTypes: ['multipart/form-data'],
                returnType: RelatedContentRepresentation
            });
        }
    }
    createTemporaryRawRelatedContent(file) {
        throwIfNotDefined(file, 'file');
        const formParams = {
            file
        };
        return this.post({
            path: '/api/enterprise/content/raw',
            formParams,
            contentTypes: ['multipart/form-data'],
            returnType: RelatedContentRepresentation
        });
    }
    createTemporaryRelatedContent(relatedContent) {
        throwIfNotDefined(relatedContent, 'relatedContent');
        return this.post({
            path: '/api/enterprise/content',
            bodyParam: relatedContent,
            returnType: RelatedContentRepresentation
        });
    }
    deleteContent(contentId) {
        throwIfNotDefined(contentId, 'contentId');
        const pathParams = {
            contentId
        };
        return this.delete({
            path: '/api/enterprise/content/{contentId}',
            pathParams
        });
    }
    getContent(contentId) {
        throwIfNotDefined(contentId, 'contentId');
        const pathParams = {
            contentId
        };
        return this.get({
            path: '/api/enterprise/content/{contentId}',
            pathParams,
            returnType: RelatedContentRepresentation
        });
    }
    getRawContentUrl(contentId) {
        return `${this.apiClient.basePath}/api/enterprise/content/${contentId}/raw`;
    }
    getRawContent(contentId, renditionType) {
        throwIfNotDefined(contentId, 'contentId');
        if (renditionType) {
            const pathParams = {
                contentId,
                renditionType
            };
            return this.get({
                path: '/api/enterprise/content/{contentId}/rendition/{renditionType}',
                pathParams,
                returnType: 'blob',
                responseType: 'blob'
            });
        }
        else {
            const pathParams = {
                contentId
            };
            return this.get({
                path: '/api/enterprise/content/{contentId}/raw',
                pathParams,
                returnType: 'blob',
                responseType: 'blob'
            });
        }
    }
    getRelatedContentForProcessInstance(processInstanceId, opts) {
        throwIfNotDefined(processInstanceId, 'processInstanceId');
        opts = opts || {};
        const pathParams = {
            processInstanceId
        };
        return this.get({
            path: '/api/enterprise/process-instances/{processInstanceId}/content',
            pathParams,
            queryParams: opts,
            returnType: ResultListDataRepresentationRelatedContentRepresentation
        });
    }
    getRelatedContentForTask(taskId, opts) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.get({
            path: '/api/enterprise/tasks/{taskId}/content',
            pathParams,
            queryParams: opts,
            returnType: ResultListDataRepresentationRelatedContentRepresentation
        });
    }
    getProcessesAndTasksOnContent(sourceId, source, size, page) {
        throwIfNotDefined(sourceId, 'sourceId');
        throwIfNotDefined(source, 'source');
        return this.get({
            path: '/api/enterprise/document-runtime',
            queryParams: {
                sourceId,
                source,
                size,
                page
            }
        });
    }
}
